import { useState, useEffect } from "react";
import { subscriptionStyle, LOGO_SRC, fmt } from "./subscription/sharedStyle";
import SquareCardForm from "./subscription/SquareCardForm";

// Defined outside the component on purpose: if it lived inside, every keystroke
// would rebuild it and wipe the Square card form.
function Shell({ children }) {
  return (
    <div className="gg-app">
      <style>{subscriptionStyle}</style>
      <header className="gg-header">
        <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
        <a href="mailto:grindgather@gmail.com" className="gg-contact-link">Get in Touch</a>
      </header>
      <div className="gg-page">{children}</div>
    </div>
  );
}

const MONTHS = (y, m) =>
  new Date(Date.UTC(y, m, 1)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

// Simple month calendar. Unavailable days are grayed out and can't be clicked.
// Defined outside the page component so typing elsewhere doesn't reset it.
function PickupCalendar({ earliest, latest, blackout, value, onChange }) {
  const [view, setView] = useState({ y: Number(earliest.slice(0, 4)), m: Number(earliest.slice(5, 7)) - 1 });
  const first = Date.UTC(view.y, view.m, 1);
  const lead = new Date(first).getUTCDay();
  const daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const ok = (dateIso) => {
    if (dateIso < earliest || dateIso > latest) return false;
    const m = Number(dateIso.slice(5, 7));
    const d = Number(dateIso.slice(8, 10));
    return !(blackout || []).some((b) => b.month === m && d >= b.fromDay);
  };
  const atStart = iso(view.y, view.m, 1) <= earliest.slice(0, 8) + "01";
  const atEnd = iso(view.y, view.m, 1) >= latest.slice(0, 8) + "01";
  const go = (delta) => setView((v) => {
    const t = new Date(Date.UTC(v.y, v.m + delta, 1));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() };
  });
  const navBtn = { background: "none", border: "1px solid #C8D5C0", width: 32, height: 32, cursor: "pointer", fontSize: 16 };

  return (
    <div style={{ maxWidth: 340 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <button type="button" style={{ ...navBtn, opacity: atStart ? 0.3 : 1 }} disabled={atStart} onClick={() => go(-1)}>‹</button>
        <strong style={{ fontWeight: 500 }}>{MONTHS(view.y, view.m)}</strong>
        <button type="button" style={{ ...navBtn, opacity: atEnd ? 0.3 : 1 }} disabled={atEnd} onClick={() => go(1)}>›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, textAlign: "center", fontSize: 11, opacity: 0.6, marginBottom: 4 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const dateIso = iso(view.y, view.m, d);
          const allowed = ok(dateIso);
          const selected = value === dateIso;
          return (
            <button
              key={i}
              type="button"
              disabled={!allowed}
              onClick={() => onChange(dateIso)}
              style={{
                height: 36,
                border: selected ? "1px solid #1E1C18" : "1px solid #E0DAD0",
                background: selected ? "#8C9E82" : "#fff",
                color: selected ? "#fff" : allowed ? "#1E1C18" : "#B5B0A5",
                opacity: allowed ? 1 : 0.45,
                cursor: allowed ? "pointer" : "not-allowed",
                fontSize: 13,
                textDecoration: allowed ? "none" : "line-through",
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Popup shown after a request is sent: the form dims behind it. Closing goes to
// the main order page, so nobody can accidentally send the same request twice.
function ConfirmationModal({ done }) {
  const close = () => { window.location.href = "/"; };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      onClick={close}
      style={{ position: "fixed", inset: 0, background: "rgba(20, 18, 16, 0.82)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cheesecake-confirm-title"
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", isolation: "isolate", background: "#FAF8F4", borderRadius: 12, maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: "32px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}
      >
        <img
          src="/flower-watermark.webp"
          alt=""
          aria-hidden="true"
          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(96%, 520px)", opacity: 0.32, pointerEvents: "none", zIndex: -1 }}
        />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          style={{ position: "absolute", top: 10, right: 14, background: "none", border: "none", fontSize: 26, lineHeight: 1, cursor: "pointer", color: "#1E1C18", opacity: 0.6, zIndex: 2 }}
        >
          ×
        </button>

        <h1 id="cheesecake-confirm-title" style={{ marginTop: 0 }}>Request received!</h1>
        <p>
          Thank you, {done.firstName}. You won't be charged until your request is confirmed. Meanwhile, your card is
          securely saved with Square. We'll email you either way, and if we are unable to fulfill your order on your
          preferred date, we will let you know!
        </p>

        <div className="gg-card" style={{ background: "rgba(242, 237, 228, 0.55)" }}>
          <div style={{ fontWeight: 500, marginBottom: 10 }}>Your request</div>
          {done.items.map((i) => (
            <div key={i.name} className="gg-item-row">
              <span>{i.name} × {i.quantity}</span>
              <span>{fmt(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="gg-item-row" style={{ fontWeight: 500, marginTop: 8 }}>
            <span>Total</span>
            <span>{fmt(done.total)}</span>
          </div>
          <div style={{ fontSize: 14, marginTop: 12 }}>
            <strong>Pickup:</strong> {done.pickupLabel}
          </div>
          {done.note && (
            <div style={{ fontSize: 14, marginTop: 6 }}>
              <strong>Your note:</strong> {done.note}
            </div>
          )}
        </div>

        <p style={{ fontSize: 13, opacity: 0.8 }}>
          A copy is on its way to your inbox. If you don't see it, check your spam folder. Questions? Just reply to that email.
        </p>
        <button className="gg-btn" type="button" onClick={close}>Back to the order page</button>
      </div>
    </div>
  );
}

// /cheesecakes — request a cheesecake. The card is saved with Square but NOT
// charged; it is only charged when Sara approves the request in admin.
export default function CheesecakePage() {
  const [menu, setMenu] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [cart, setCart] = useState({});
  const [pickupDate, setPickupDate] = useState("");
  const [info, setInfo] = useState({ name: "", email: "", phone: "", note: "" });
  const [website, setWebsite] = useState(""); // honeypot, humans never see it
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch("/api/cheesecake")
      .then((r) => r.json())
      .then(setMenu)
      .catch(() => setLoadError("We couldn't load the menu. Please refresh and try again."));
  }, []);

  const totalQty = Object.values(cart).reduce((a, b) => a + b, 0);
  const maxPerRequest = menu?.maxPerRequest || 4;
  const isHolidayPick = (menu?.holidays || []).some((h) => h.dates.some((d) => d.date === pickupDate));
  const setQty = (id, qty) =>
    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });

  const items = Object.entries(cart).map(([id, quantity]) => ({ id, quantity }));
  const total = (menu?.flavors || []).reduce((sum, f) => sum + f.price * (cart[f.id] || 0), 0);
  const canSubmit =
    items.length > 0 &&
    pickupDate &&
    info.name.trim() &&
    info.email.trim() &&
    info.phone.trim() &&
    consent;

  const handleTokenize = async (token, tokenError) => {
    if (tokenError) { setSubmitError(tokenError); return; }
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/cheesecake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "request",
          ...info,
          website,
          items,
          pickupDate,
          consent,
          cardNonce: token,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setDone({ pickupLabel: data.pickupLabel, items: data.items || [], total: data.total, note: data.note, firstName: (info.name.trim().split(/\s+/)[0] || "").replace(/^./, (c) => c.toUpperCase()) });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) return <Shell><p>{loadError}</p></Shell>;
  if (!menu) return <Shell><p className="gg-loading">Loading…</p></Shell>;

  if (!menu.open) {
    return (
      <Shell>
        <h1>Cheesecakes</h1>
        <p>Cheesecake requests are closed right now. Check back soon, or email us at <a href="mailto:grindgather@gmail.com">grindgather@gmail.com</a>.</p>
      </Shell>
    );
  }

  if (menu.spotsLeft <= 0) {
    return (
      <Shell>
        <h1>Cheesecakes</h1>
        <p>We're fully booked for cheesecakes right now. Check back soon, or email us at <a href="mailto:grindgather@gmail.com">grindgather@gmail.com</a>.</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1>Homemade Cheesecakes</h1>
      <p>Baked from scratch in small batches. Each cheesecake is a 9-inch round, 3 inches tall, and serves at least 16.</p>
      <p>This is a request, not an instant order. Pick your cheesecake and a pickup date, and we'll confirm before your card is charged.</p>

      <div className="gg-section-divider" />

      <h2>Choose Your Cheesecake</h2>
      <div className="gg-card">
        {menu.flavors.map((f) => {
          const qty = cart[f.id] || 0;
          return (
            <div key={f.id} style={{ marginBottom: 18 }}>
              <div className="gg-item-row">
                <span>{f.name} — {fmt(f.price)}</span>
                {f.soldOut ? (
                  <span style={{ opacity: 0.6 }}>Not available</span>
                ) : (
                  <div className="gg-qty-control">
                    <button className="gg-qty-btn" onClick={() => setQty(f.id, qty - 1)} disabled={qty === 0}>−</button>
                    <span style={{ minWidth: 18, textAlign: "center" }}>{qty}</span>
                    <button className="gg-qty-btn" onClick={() => setQty(f.id, qty + 1)} disabled={totalQty >= maxPerRequest}>+</button>
                  </div>
                )}
              </div>
              {f.description && <div style={{ fontSize: 13, fontWeight: 300, lineHeight: 1.6, marginTop: 4 }}>{f.description}</div>}
              {f.ingredients && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{f.ingredients}</div>}
            </div>
          );
        })}
        {items.length > 0 && (
          <div className="gg-item-row" style={{ fontWeight: 500 }}>
            <span>Total</span>
            <span>{fmt(total)}</span>
          </div>
        )}
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>Up to {maxPerRequest} cheesecakes per request.</div>
      </div>

      <h2>Pickup Date</h2>
      {(menu.holidays || []).map((h) => (
        <div key={h.name} className="gg-card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>{h.name} pickup dates</div>
          {h.dates.map((d) => (
            <label key={d.date} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: "pointer", fontSize: 14 }}>
              <input
                type="checkbox"
                checked={pickupDate === d.date}
                onChange={() => setPickupDate(pickupDate === d.date ? "" : d.date)}
                style={{ accentColor: "#8C9E82", width: 16, height: 16 }}
              />
              {d.label}
            </label>
          ))}
        </div>
      ))}

      <div className="gg-card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{(menu.holidays || []).length > 0 ? "Or pick another date" : "Pick a date"}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
          Any date at least {menu.minDays} days from today. Grayed-out dates aren't available, and late November and December are kept for the holiday pickup dates above.
        </div>
        <PickupCalendar
          earliest={menu.earliestDate}
          latest={menu.latestDate}
          blackout={menu.blackout}
          value={isHolidayPick ? "" : pickupDate}
          onChange={setPickupDate}
        />
      </div>
      {pickupDate && (
        <p style={{ fontSize: 13 }}>
          Selected pickup date: <strong>{new Date(pickupDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</strong>. It isn't booked until we confirm it.
        </p>
      )}

      <div className="gg-form-group">
        <label className="gg-form-label">Special requests (optional)</label>
        <textarea
          className="gg-form-input"
          rows={3}
          maxLength={500}
          placeholder="Ex: a requested date, or after 5pm"
          value={info.note}
          onChange={(e) => setInfo((p) => ({ ...p, note: e.target.value }))}
        />
      </div>

      <h2>Your Info</h2>
      {[
        { key: "name", label: "Full Name", type: "text", placeholder: "Sara Smith" },
        { key: "email", label: "Email Address", type: "email", placeholder: "sara@example.com" },
        { key: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 000-0000" },
      ].map((field) => (
        <div key={field.key} className="gg-form-group">
          <label className="gg-form-label">{field.label}</label>
          <input
            className="gg-form-input"
            type={field.type}
            placeholder={field.placeholder}
            value={info[field.key]}
            onChange={(e) => setInfo((p) => ({ ...p, [field.key]: e.target.value }))}
          />
        </div>
      ))}

      {/* Honeypot: hidden from people, bots fill it in */}
      <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
        <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
      </div>

      <div className="gg-section-divider" />
      <h2>Card on File</h2>
      <p>
        You won't be charged until your request is confirmed. Meanwhile, your card is securely saved with Square.
        Either way, you'll get an email from us. If we are unable to fulfill your order on your preferred date, we will let you know!
      </p>

      <div className="gg-card" style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <input
          type="checkbox"
          id="cheesecakeConsent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ marginTop: 2, accentColor: "#8C9E82", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
        />
        <label htmlFor="cheesecakeConsent" style={{ fontSize: 13, fontWeight: 300, color: "#1E1C18", lineHeight: 1.6, cursor: "pointer" }}>
          I understand that if Grind &amp; Gather confirms this order, my card will be charged {items.length > 0 ? fmt(total) : "the total shown"} at that time. If it isn't confirmed, I won't be charged.
        </label>
      </div>

      {canSubmit ? (
        <SquareCardForm
          onTokenize={handleTokenize}
          loading={submitting}
          error={submitError}
          buttonLabel={`Send Request · ${fmt(total)} (not charged yet)`}
        />
      ) : (
        <div className="gg-card">
          <p style={{ marginBottom: 0, opacity: 0.6 }}>Choose a cheesecake and a pickup date, fill in your info, and check the box above to continue.</p>
        </div>
      )}
      {done && <ConfirmationModal done={done} />}
    </Shell>
  );
}
